import React, { useState } from 'react';
import { X, Eye, Trash2 } from 'lucide-react';
import styled from 'styled-components';
import { Modal } from '../Modal';
import {
  FilePreviewSection,
  FileGrid,
  FilePreviewCard,
  FilePreview,
  FileCardName,
  FileImage,
  FileIcon,
  FileActions,
  ActionButton,
} from './FileUpload.styled';
import { useImageWithFallback } from '../../../hooks/useImageWithFallback';

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80px;
  background: #fef2f2;
  border: 1px solid var(--color-error, #f04438);
  border-radius: var(--radius-lg);
  padding: 8px;
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--color-error, #f04438);
  text-align: center;
  margin-top: 4px;
`;

const ShimmerWrapper = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
`;

const GlobalStyle = styled.div`
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const FileImageWithFallback: React.FC<{ src: string; alt: string }> = ({
  src,
  alt,
}) => {
  const { imageUrl, isLoading } = useImageWithFallback(src);

  if (isLoading) {
    return <ShimmerWrapper />;
  }

  return <FileImage src={imageUrl} alt={alt} />;
};

export interface FileUploadState {
  id: string;
  file: File;
  preview?: string;
  status:
    | 'initial'
    | 'pending'
    | 'uploading'
    | 'completed'
    | 'error'
    | 'deleting';
  progress: number;
  error?: string;
  uploadResult?: any;
  attachmentId?: string;
  previewUrl?: string;
}

interface FileManagerProps {
  files: FileUploadState[];
  onRemoveFile: (fileId: string) => void;
  onViewFile: (file: FileUploadState) => void;
  disabled?: boolean;
}

export default function FileManager({
  files,
  onRemoveFile,
  onViewFile,
  disabled = false,
}: FileManagerProps) {
  const [fileToDelete, setFileToDelete] = useState<FileUploadState | null>(
    null
  );

  const handleConfirmDelete = () => {
    if (fileToDelete) {
      onRemoveFile(fileToDelete.id);
      setFileToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setFileToDelete(null);
  };

  const renderFileIcon = (file: File) => {
    if (file.type.includes('pdf')) {
      return (
        <FileIcon>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        </FileIcon>
      );
    }
    return (
      <FileIcon>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      </FileIcon>
    );
  };

  const renderFilePreview = (fileState: FileUploadState) => {
    if (fileState.status === 'error') {
      return (
        <ErrorState>
          <X size={16} color="var(--color-error, #f04438)" />
          <ErrorText>Upload Failed</ErrorText>
        </ErrorState>
      );
    }

    // Show uploading state with progress
    if (fileState.status === 'uploading') {
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {renderFilePreviewContent(fileState)}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '2px solid #ffffff40',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '4px',
              }}
            />
            {/* {fileState.progress}% */}
          </div>
        </div>
      );
    }

    // Show deleting state
    if (fileState.status === 'deleting') {
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {renderFilePreviewContent(fileState)}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '2px solid #ffffff40',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '4px',
              }}
            />
            Deleting...
          </div>
        </div>
      );
    }

    return renderFilePreviewContent(fileState);
  };

  const renderFilePreviewContent = (fileState: FileUploadState) => {
    // Show actual preview for all states (initial, pending, completed)
    const fileName = fileState.file.name;
    const isImageFile =
      fileState.file.type.startsWith('image/') ||
      fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isVideoFile =
      fileState.file.type.startsWith('video/') ||
      fileName.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);

    if (isImageFile && (fileState.preview || fileState.previewUrl)) {
      return (
        <FileImageWithFallback
          src={fileState.preview || fileState.previewUrl || ''}
          alt={fileName}
        />
      );
    }

    // For video files, show video thumbnail with play button
    if (isVideoFile && (fileState.preview || fileState.previewUrl)) {
      const videoUrl = fileState.preview || fileState.previewUrl || '';
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
          }}
        >
          <video
            src={videoUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            muted
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      );
    }

    return renderFileIcon(fileState.file);
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <>
      <GlobalStyle />
      <FilePreviewSection>
        <FileGrid>
          {files.map(fileItem => (
            <FilePreviewCard key={fileItem.id}>
              <FilePreview>{renderFilePreview(fileItem)}</FilePreview>
              {/* FileActions is placed directly on FilePreviewCard (outside overflow:hidden FilePreview)
                  to fix a Safari bug where pointer events are misaligned inside overflow:hidden ancestors */}
              {(fileItem.status === 'completed' ||
                fileItem.status === 'initial' ||
                fileItem.status === 'pending') && (
                <FileActions>
                  <ActionButton
                    onClick={e => {
                      e.stopPropagation();
                      onViewFile(fileItem);
                    }}
                    title="View File"
                  >
                    <Eye size={14} />
                  </ActionButton>
                  {!disabled && (
                    <ActionButton
                      onClick={e => {
                        e.stopPropagation();
                        setFileToDelete(fileItem);
                      }}
                      title="Delete File"
                    >
                      <Trash2 size={14} />
                    </ActionButton>
                  )}
                </FileActions>
              )}
              {fileItem.status === 'error' && (
                <FileActions>
                  <ActionButton
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveFile(fileItem.id);
                    }}
                    title="Remove File"
                  >
                    <Trash2 size={14} />
                  </ActionButton>
                </FileActions>
              )}
              <FileCardName title={fileItem.file.name}>
                {fileItem.file.name.length > 15
                  ? `${fileItem.file.name.substring(0, 12)}...`
                  : fileItem.file.name}
              </FileCardName>
            </FilePreviewCard>
          ))}
        </FileGrid>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!fileToDelete}
          onClose={handleCancelDelete}
          title="Delete File"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete "
              {(fileToDelete?.file.name?.length || 0) > 20 ? (
                <>{fileToDelete?.file.name?.slice(0, 20)}...?</>
              ) : (
                fileToDelete?.file.name
              )}
              " This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      </FilePreviewSection>
    </>
  );
}
